import config from 'config';
import pinterpolate from 'pinterpolate';
import { pulse } from 'assets/images';
import React, { useEffect, useMemo, useRef } from 'react';
import { SecondaryNav } from '@vyaguta/vyaguta-ui';
import { Route, Redirect, Switch } from 'react-router-dom';

import useQuery from 'hooks/useQuery';

import interpolate from 'pinterpolate';

import LeaveCredits from '../leaveCredits/LeaveCredits';
import EmployeeGoals from '../goals/EmployeeGoals';
import EmployeeAssets from '../assets/EmployeeAssets';
import * as permissions from 'constants/permissions';
import CreateEditNote from '../notes/CreateEditNote';
import ProfileDescription from './ProfileDescription';
import Loading from 'components/common/loading/Loading';
import AuthorizedRoute from 'pages/AuthorizedRoute';
import NoteList from 'pages/employees/profile/notes/NoteList';

import { en } from 'constants/lang/en';
import * as routes from 'constants/routes';
import { ROLE } from 'constants/appConstants';

import { buildUrl } from 'utils/string';
import { handleError } from 'utils/errorHandler';

import { useFetchLeaveBalance } from 'hooks/query/useFetchLeaveBalance';
import { useFetchFiscalYearByCountry } from 'hooks/query/useFetchFiscalYearByCountry';
import { useMyRoleQuery } from 'hooks/query/useMyRole';
import { useUserPermissions } from 'hooks/query/useUserInfo';
import { RouteComponentProps } from 'react-router-dom';
import { DateInput } from 'interface/common';

interface ProfileEmployee {
  id: number;
  country?: string;
  leaveIssuer?: { id: number };
  resignation?: DateInput;
}

interface AuthUser {
  id: number;
}

interface FiscalYear {
  id: number;
  isCurrent: boolean;
}

interface ProfileBodyProps extends RouteComponentProps<{ id?: string }> {
  authUser: AuthUser;
  employee: ProfileEmployee;
}

const ProfileBody: React.FC<ProfileBodyProps> = (props) => {
  const { authUser, employee } = props;
  const { data: authUserRoles = [], isLoading: isAuthUserRolesLoading } = useMyRoleQuery(authUser.id);

  const [query, setQuery] = useQuery(props) as [
    { period?: string; year?: string | number; type?: string },
    (value: { period?: string; year?: string | number; type?: string }) => void,
  ];

  const userPermissions = useUserPermissions();

  const canView = Boolean(userPermissions[permissions.NOTES.CREATE]);

  const isSelf = authUser.id === employee.id;
  const canViewAssets = isSelf || Boolean(userPermissions[permissions.EMPLOYEES.ASSETS.VIEW]);

  const canApplyLeave = useMemo(
    () =>
      Boolean(userPermissions[permissions.LEAVE.CREATE]) ||
      authUser.id === employee.id ||
      (employee.leaveIssuer && authUser.id === employee.leaveIssuer.id),
    [authUser.id, employee.id, employee.leaveIssuer, userPermissions]
  );

  const canViewLeaveBalance = Boolean(
    userPermissions[permissions.LEAVE.LEAVE_BALANCE.VIEW] || isSelf || authUser.id === employee.leaveIssuer?.id
  );

  const {
    data: fiscalYears,
    isError: isFiscalYearsError,
    error: fiscalYearsError,
    errorUpdatedAt: fiscalYearsErrorUpdatedAt,
  } = useFetchFiscalYearByCountry(employee.country, canViewLeaveBalance) as {
    data?: FiscalYear[];
    isError: boolean;
    error: Error | null;
    errorUpdatedAt: number;
  };

  const currentFiscalYear = useMemo(() => {
    if (!fiscalYears) {
      return;
    }

    return fiscalYears.find((fiscalYear) => fiscalYear.isCurrent);
  }, [fiscalYears]);

  const {
    data: leaveBalance,
    isError: isLeaveBalanceError,
    error: leaveBalanceError,
    errorUpdatedAt: leaveBalanceErrorUpdatedAt,
    isFetched,
    isLoading,
  } = useFetchLeaveBalance(
    {
      userId: employee.id,
      fiscalYearId: currentFiscalYear?.id,
    },
    canViewLeaveBalance
  );
  const lastHandledFiscalYearsErrorAtRef = useRef(0);
  const lastHandledLeaveBalanceErrorAtRef = useRef(0);

  useEffect(() => {
    if (
      !isFiscalYearsError ||
      !fiscalYearsError ||
      fiscalYearsErrorUpdatedAt <= lastHandledFiscalYearsErrorAtRef.current
    ) {
      return;
    }

    handleError(fiscalYearsError as object);
    lastHandledFiscalYearsErrorAtRef.current = fiscalYearsErrorUpdatedAt;
  }, [isFiscalYearsError, fiscalYearsError, fiscalYearsErrorUpdatedAt]);

  useEffect(() => {
    if (
      !isLeaveBalanceError ||
      !leaveBalanceError ||
      leaveBalanceErrorUpdatedAt <= lastHandledLeaveBalanceErrorAtRef.current
    ) {
      return;
    }

    handleError(leaveBalanceError as object);
    lastHandledLeaveBalanceErrorAtRef.current = leaveBalanceErrorUpdatedAt;
  }, [isLeaveBalanceError, leaveBalanceError, leaveBalanceErrorUpdatedAt]);

  const navigationList = [
    {
      label: 'Summary',
      to: pinterpolate(routes.EMPLOYEE_PROFILE, { id: employee.id }),
    },
    ...(!authUserRoles.includes(ROLE.SUB_CONTRACTOR)
      ? [
          {
            label: 'Goals/Achievements',
            to: pinterpolate(routes.EMPLOYEE_PROFILE_GOALS, { id: employee.id }),
            visible: !authUserRoles.includes(ROLE.SUB_CONTRACTOR),
          },
        ]
      : []),
    // {
    //   label: 'Feedbacks',
    //   to: pinterpolate(routes.EMPLOYEE_PROFILE_FEEDBACKS, { id: employee.id })
    // },
    ...(leaveBalance?.length
      ? [
          {
            label: 'Leave Credits',
            visible: leaveBalance?.length !== 0,
            to: pinterpolate(routes.EMPLOYEE_PROFILE_LEAVE_CREDITS, { id: employee.id }),
          },
        ]
      : []),
    ...(canView
      ? [
          {
            exact: false,
            label: 'Notes',
            to: pinterpolate(routes.EMPLOYEE_NOTE, { userId: employee.id }),
          },
        ]
      : []),
    ...(canViewAssets
      ? [
          {
            label: 'Assets',
            to: pinterpolate(routes.EMPLOYEE_ASSETS, { id: employee.id }),
          },
        ]
      : []),
  ];

  if ((!isFetched || isLoading || isAuthUserRolesLoading) && canViewLeaveBalance) {
    return (
      <div className="container profile-body-container">
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <div id="profile-body-container">
        {navigationList.length > 0 && (
          <div className="profile-body-nav container mt-15">
            <SecondaryNav navItems={navigationList} color="primary" className="profile-body-nav__secondary">
              <a
                href={interpolate(buildUrl(config.pulseBaseUrl, config.endpoints.pulse.userFeed), {
                  id: employee.id,
                })}
                className="profile-body-nav__pulse"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={pulse} alt="pulse_logo" />
                <span>{en.PULSE}</span>
              </a>
            </SecondaryNav>
          </div>
        )}
        <Switch>
          <Route
            exact
            path={routes.EMPLOYEE_PROFILE}
            render={() => <ProfileDescription authUser={authUser} employee={employee as never} />}
          />
          <Route
            exact
            path={routes.EMPLOYEE_PROFILE_LEAVE_CREDITS}
            render={() => (
              <LeaveCredits
                data={leaveBalance}
                employeeId={employee.id}
                canApplyLeave={canApplyLeave}
                resignation={employee.resignation}
                fiscalYearId={currentFiscalYear?.id}
              />
            )}
          />
          <Route exact path={routes.EMPLOYEE_ASSETS} render={() => <EmployeeAssets employeeId={employee.id} />} />
          {/* <Route
              exact
              path={routes.EMPLOYEE_PROFILE_FEEDBACKS}
              render={() => <EmployeeFeedbacks employeeId={employee.id} query={query} setQuery={setQuery} />}
            /> */}
          {/* <AuthorizedRoute
              exact
              path={routes.EMPLOYEE_PROFILE_MY_TEAM}
              allowSelf={employee.id === authUser.id}
              component={EmployeeTeams}
              {...props}
            /> */}
          <AuthorizedRoute
            exact
            path={routes.EMPLOYEE_PROFILE_GOALS}
            allowSelf={false}
            customClassName=""
            forbiddenRoles={[ROLE.SUB_CONTRACTOR]}
            render={() => <EmployeeGoals employeeId={employee.id} query={query} setQuery={setQuery} />}
          />
          <AuthorizedRoute
            exact
            permission={[permissions.NOTES.VIEW]}
            allowSelf={false}
            customClassName=""
            forbiddenRoles={[]}
            path={routes.EMPLOYEE_NOTE}
            component={NoteList}
            {...props}
          />
          <AuthorizedRoute
            exact
            permission={[permissions.NOTES.CREATE]}
            allowSelf={false}
            customClassName=""
            forbiddenRoles={[]}
            path={pinterpolate(routes.CREATE_EMPLOYEE_NOTE, { userId: employee.id })}
            component={(props) => <CreateEditNote {...props} />}
          />
          <AuthorizedRoute
            exact
            permission={[permissions.NOTES.CREATE]}
            allowSelf={false}
            customClassName=""
            forbiddenRoles={[]}
            path={pinterpolate(routes.UPDATE_EMPLOYEE_NOTE, { userId: employee.id })}
            component={(props) => <CreateEditNote {...props} />}
          />
          <Redirect to={routes.ACTIVE_EMPLOYEES} />
        </Switch>
      </div>
    </div>
  );
};

export default ProfileBody;
